/** @jsxImportSource theme-ui */
import React from 'react';
import { Box, Button, Text, Accordion } from '@infotrack/zenith-ui';
import type { ImportTopFiveResponse } from '../types/import.types';

interface ImportResultsSummaryProps {
  results: ImportTopFiveResponse;
  onStartNew: () => void;
}

/**
 * Displays import results with success/failure statistics and detailed error information.
 */
export const ImportResultsSummary: React.FC<ImportResultsSummaryProps> = ({ results, onStartNew }) => {
  const successRate =
    (results.totalRowsProcessed ?? 0) > 0
      ? Math.round(((results.successfulRows ?? 0) / (results.totalRowsProcessed ?? 0)) * 100)
      : 0;

  const hasRowErrors = results.rowErrors && Object.keys(results.rowErrors).length > 0;
  const hasGlobalErrors = results.globalErrors && results.globalErrors.length > 0;
  const hasWarnings = results.warnings && results.warnings.length > 0;

  // Group errors by type for summary
  const errorSummary = React.useMemo(() => {
    if (!results.rowErrors) return [];
    
    const summary: Record<string, number> = {};
    Object.values(results.rowErrors).forEach((errors) => {
      errors.forEach((error) => {
        const key = error.split(':')[0] || 'Unknown'; // Get error type before colon
        summary[key] = (summary[key] || 0) + 1;
      });
    });
    return Object.entries(summary)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5); // Top 5 error types
  }, [results.rowErrors]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* Summary Header */}
      <Box sx={{ textAlign: 'center' }}>
        <Box sx={{ marginBottom: 2 }}>
          <Text variant="title" as="h3">
            {results.hasErrors ? 'Import Completed with Errors' : 'Import Successful!'}
          </Text>
        </Box>
        <Box sx={{ color: 'neutral/600' }}>
          <Text variant="medium">{results.summaryMessage ?? 'Import completed'}</Text>
        </Box>
      </Box>

      {/* Statistics Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: ['1fr', '1fr 1fr', '1fr 1fr 1fr 1fr'],
          gap: 3,
        }}
      >
        <Box
          sx={{
            padding: 3,
            backgroundColor: 'neutral/50',
            borderRadius: 'medium',
            textAlign: 'center',
          }}
        >
          <Box sx={{ marginBottom: 1 }}>
            <Text variant="hero">{results.totalRowsProcessed ?? 0}</Text>
          </Box>
          <Box sx={{ color: 'neutral/600' }}>
            <Text variant="caption">Total Rows</Text>
          </Box>
        </Box>

        <Box
          sx={{
            padding: 3,
            backgroundColor: 'success/10',
            borderRadius: 'medium',
            textAlign: 'center',
          }}
        >
          <Box sx={{ color: 'success', marginBottom: 1 }}>
            <Text variant="hero">{results.successfulRows ?? 0}</Text>
          </Box>
          <Box sx={{ color: 'neutral/600' }}>
            <Text variant="caption">Successful</Text>
          </Box>
        </Box>

        <Box
          sx={{
            padding: 3,
            backgroundColor: (results.failedRows ?? 0) > 0 ? 'error/10' : 'neutral/50',
            borderRadius: 'medium',
            textAlign: 'center',
          }}
        >
          <Box sx={{ color: (results.failedRows ?? 0) > 0 ? 'error' : 'neutral/600', marginBottom: 1 }}>
            <Text variant="hero">{results.failedRows ?? 0}</Text>
          </Box>
          <Box sx={{ color: 'neutral/600' }}>
            <Text variant="caption">Failed</Text>
          </Box>
        </Box>

        <Box
          sx={{
            padding: 3,
            backgroundColor: 'info/10',
            borderRadius: 'medium',
            textAlign: 'center',
          }}
        >
          <Box sx={{ color: 'info', marginBottom: 1 }}>
            <Text variant="hero">{successRate}%</Text>
          </Box>
          <Box sx={{ color: 'neutral/600' }}>
            <Text variant="caption">Success Rate</Text>
          </Box>
        </Box>
      </Box>

      {/* Warnings */}
      {hasWarnings && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: 'warning/10',
            borderRadius: 'medium',
            borderLeft: '4px solid',
            borderLeftColor: 'warning',
          }}
        >
          <Box sx={{ marginBottom: 2 }}>
            <Text variant="subtitle">⚠️ Warnings</Text>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {results.warnings?.map((warning, index) => (
              <Box key={index} sx={{ color: 'warning/700' }}>
                <Text variant="medium">• {warning}</Text>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Global Errors */}
      {hasGlobalErrors && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: 'error/10',
            borderRadius: 'medium',
            borderLeft: '4px solid',
            borderLeftColor: 'error',
          }}
        >
          <Box sx={{ marginBottom: 2 }}>
            <Text variant="subtitle">Import Errors</Text>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {results.globalErrors?.map((error, index) => (
              <Box key={index} sx={{ color: 'error' }}>
                <Text variant="medium">• {error}</Text>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Common Errors Summary */}
      {errorSummary.length > 0 && (
        <Box>
          <Box sx={{ marginBottom: 2 }}>
            <Text variant="subtitle">Most Common Errors</Text>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {errorSummary.map(([errorType, count]) => (
              <Box
                key={errorType}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: 2,
                  backgroundColor: 'warning/10',
                  borderRadius: 'small',
                }}
              >
                <Text variant="medium">{errorType}</Text>
                <Box
                  sx={{
                    padding: '4px 12px',
                    backgroundColor: 'warning',
                    color: 'white',
                    borderRadius: 'full',
                  }}
                >
                  <Box sx={{ fontWeight: 'bold' }}>
                    <Text variant="caption">{count}</Text>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Detailed Row Errors */}
      {hasRowErrors && results.rowErrors && (
        <Box>
          <Box sx={{ marginBottom: 2 }}>
            <Text variant="subtitle">Row-Level Errors ({Object.keys(results.rowErrors).length} rows affected)</Text>
          </Box>
          <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
            {Object.entries(results.rowErrors)
              .sort(([a], [b]) => Number(a) - Number(b))
              .map(([rowNumber, errors]) => (
                <Box key={rowNumber} sx={{ marginBottom: 2 }}>
                  <Accordion title={`Row ${rowNumber}`} expanded={false}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, padding: 2 }}>
                      {errors.map((error, index) => (
                        <Box key={index} sx={{ color: 'error' }}>
                          <Text variant="small">• {error}</Text>
                        </Box>
                      ))}
                    </Box>
                  </Accordion>
                </Box>
              ))}
          </Box>
        </Box>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
        <Button onClick={onStartNew} variant="primary">
          Import Another File
        </Button>
      </Box>
    </Box>
  );
};
